import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { colors, fonts, layout, spacing } from '../theme';
import Icon from './Icon';

const styles = {
  dropzone: {
    border: `2px dashed ${colors.borderColor}`,
    borderRadius: layout.borderRadius,
    padding: spacing.xl,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: colors.white,
  },
  dropzoneActive: {
    borderColor: colors.blue,
    backgroundColor: colors.gray50,
  },
  dropzoneReject: {
    borderColor: colors.error,
    backgroundColor: '#fff5f5',
  },
  icon: {
    marginBottom: spacing.md,
    color: colors.midGray,
  },
  text: {
    fontFamily: fonts.body,
    fontSize: '1rem',
    color: colors.darkGray,
    marginBottom: spacing.xs,
  },
  hint: {
    fontFamily: fonts.body,
    fontSize: '0.875rem',
    color: colors.midGray,
  },
  fileList: {
    marginTop: spacing.md,
    textAlign: 'left',
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.sm,
    backgroundColor: colors.gray50,
    borderRadius: layout.borderRadius,
    marginBottom: spacing.xs,
  },
  fileName: {
    fontFamily: fonts.body,
    fontSize: '0.875rem',
    color: colors.darkGray,
    flex: 1,
    marginRight: spacing.sm,
  },
  fileSize: {
    fontFamily: fonts.body,
    fontSize: '0.75rem',
    color: colors.midGray,
    marginRight: spacing.sm,
  },
  error: {
    color: colors.error,
    fontSize: '0.875rem',
    marginTop: spacing.xs,
  },
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

function FileUploadZone({
  onFilesSelected,
  maxFiles = 1,
  maxSize = MAX_FILE_SIZE,
  accept = ACCEPTED_FILE_TYPES,
  multiple = false,
  disabled = false,
}) {
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState([]);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      setErrors([]);

      if (rejectedFiles.length > 0) {
        const newErrors = rejectedFiles.map(({ file, errors }) => {
          if (errors.some((e) => e.code === 'file-too-large')) {
            return `${file.name} is too large. Maximum size is ${(maxSize / 1024 / 1024).toFixed(0)}MB.`;
          }
          if (errors.some((e) => e.code === 'file-invalid-type')) {
            return `${file.name} is not an accepted file type.`;
          }
          return `${file.name}: ${errors.map((e) => e.message).join(', ')}`;
        });
        setErrors(newErrors);
      }

      const validFiles = acceptedFiles.map((file) => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
      }));

      const allFiles = multiple ? [...files, ...validFiles] : validFiles;
      const limitedFiles = allFiles.slice(0, maxFiles);

      setFiles(limitedFiles);
      onFilesSelected(limitedFiles.map((f) => f.file));
    },
    [files, maxFiles, maxSize, multiple, onFilesSelected]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: multiple || maxFiles > 1,
    disabled,
  });

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    onFilesSelected(newFiles.map((f) => f.file));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const dropzoneStyle = {
    ...styles.dropzone,
    ...(isDragActive && !isDragReject ? styles.dropzoneActive : {}),
    ...(isDragReject ? styles.dropzoneReject : {}),
    ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
  };

  return (
    <div>
      <div {...getRootProps()} style={dropzoneStyle}>
        <input {...getInputProps()} disabled={disabled} />
        <Icon name="upload" type="action" size={48} style={styles.icon} />
        <p style={styles.text}>
          {isDragActive
            ? isDragReject
              ? 'Some files are not accepted'
              : 'Drop files here'
            : `Drag & drop files here, or click to select`}
        </p>
        <p style={styles.hint}>
          {multiple || maxFiles > 1
            ? `Up to ${maxFiles} files, max ${(maxSize / 1024 / 1024).toFixed(0)}MB each`
            : `Max ${(maxSize / 1024 / 1024).toFixed(0)}MB`}
        </p>
      </div>

      {files.length > 0 && (
        <div style={styles.fileList}>
          {files.map((fileItem, index) => (
            <div key={index} style={styles.fileItem}>
              <Icon name="document" type="document" size={20} style={{ marginRight: spacing.sm }} />
              <span style={styles.fileName}>{fileItem.name}</span>
              <span style={styles.fileSize}>{formatFileSize(fileItem.size)}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: spacing.xs,
                  color: colors.error,
                }}
                aria-label={`Remove ${fileItem.name}`}
              >
                <Icon name="delete" type="action" size={18} />
              </button>
            </div>
          ))}
        </div>
      )}

      {errors.length > 0 && (
        <div>
          {errors.map((error, index) => (
            <div key={index} style={styles.error} role="alert">
              {error}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FileUploadZone;

